import {
  FC,
  useCallback,
  useMemo,
  useState,
} from "react";
import { usePostHog } from "posthog-js/react";
import { useCookies } from "react-cookie";

import {
  AddProductCardFieldName,
  AddProductField,
  AddressValue,
  FieldData,
  IPlanSummaryResponse,
} from "@forms/schema";
import {
  getCurrentFlow,
  getFieldValue,
  getFlowFieldValuesForInterpolation,
  useCustomerStore,
  useDiscountStore,
  useNewFormsServiceStore,
} from "@./state";
import {
  getFlowFieldValuesForAction,
  getFlowProduct,
} from "libs/state/src/lib/stores/useFormServiceStore";
import Column from "apps/website/components/layout/Column/Column";
import {
  ImageMax,
  getStoryBlokImage,
} from "apps/website/utils/storyblok/image";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import Button from "apps/website/components/base/Button/Button";
import Grid from "apps/website/components/layout/Grid/Grid";
import {
  SectionOneClickCard,
} from "apps/website/components/section/SectionOneClickCard/SectionOneClickCard";
import { renderStoryBlokRichText } from "apps/website/utils/storyblok/text";
import {
  IComparisonCardProps,
} from "apps/website/components/feature/ComparisonCardList/ComparisonCard/ComparisonCard";
import {
  SectionOneClickCardB,
} from "apps/website/components/section/SectionOneClickCardB/SectionOneClickCardB";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { useTrackPageView } from "apps/website/contexts/tracking";
import { useAPI } from "apps/website/hooks/useAPI";
import { useDatadog } from "@auth/client-sdk-react";
import {
  ACTION_TRACKING_COOKIE_NAMES,
  getActionTrackingInfo,
} from "apps/website/utils/tracking/getActionTrackingInfo";
import { interpolateString, Interpolator } from "@./form-utils";
import {
  getAddProductProductsToDisplay,
} from "../../../../utils/forms/addProduct";

const defaultProductTitle: Record<AddProductCardFieldName, string> = {
  sprinklesQuantity: "Chicken Sprinkles",
  nibblesChickenQuantity: "Chicken Nibbles",
  nibblesSalmonQuantity: "Salmon Nibbles",
  nibblesTrioBundleQuantity: "Nibbles Trio Bundle - 100% Meat + Fish",
};

export interface IFormServiceAddProductCardList {
  field: AddProductField;
  flowId: string;
  linkingId: string;
  next(): void;
  summaryAction?: string;
}

export const FormServiceAddProduct: FC<IFormServiceAddProductCardList> = ({
  field,
  flowId,
  linkingId,
  next,
  summaryAction,
}) => {
  const { setFlowFieldValue } = useNewFormsServiceStore();
  const { discountCodes } = useDiscountStore();
  const { customerIds } = useCustomerStore();
  const formsActionsAPI = useAPI().FormActions;
  const datadog = useDatadog();
  const tracking = useTrackPageView();
  const [ isInBasket, setIsInBasket ] = useState<boolean | null>(null);
  const posthog = usePostHog();
  const [ cookies ] = useCookies(ACTION_TRACKING_COOKIE_NAMES);

  const getTrimmedStringFieldValue = useCallback((fieldName: string): string | undefined => {
    const submitValue = getFieldValue(flowId, fieldName, "0")?.data?.submitValue;
    if (typeof submitValue !== "string") {
      return undefined;
    }
    const trimmedValue = submitValue.trim();
    return trimmedValue || undefined;
  }, [ flowId ]);

  const getFlowContactDetails = useCallback(() => {
    const address = getFieldValue(flowId, "address", "0")?.data?.submitValue as AddressValue | undefined;
    return {
      customerId: customerIds.find((dc) => dc.flowId === flowId)?.customerId?.trim(),
      customerEmail: getTrimmedStringFieldValue("emailAddress"),
      customerFirstName: getTrimmedStringFieldValue("firstName"),
      customerLastName: getTrimmedStringFieldValue("lastName"),
      postcode: getTrimmedStringFieldValue("postcode"),
      city: address?.shippingAddress?.city?.trim() || undefined,
      phoneNumber: address?.shippingAddress?.phone?.trim() || undefined,
    };
  }, [ customerIds, flowId, getTrimmedStringFieldValue ]);

  const getPlanSummaryForAddToCart = useCallback(async (): Promise<IPlanSummaryResponse | undefined> => {
    if (!summaryAction) {
      return undefined;
    }

    const featureFlags = posthog.featureFlags.getFlagVariants();
    const discountCode = discountCodes.find((dc) => dc.flowId === flowId)?.discountCode;
    const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowId);
    if (discountCode) {
      fieldSubmitValuesMap.set("discountCode", { value: discountCode });
    }
    const summaryResult = await formsActionsAPI.performAction<IPlanSummaryResponse>(
      summaryAction,
      fieldSubmitValuesMap,
      getFlowProduct(flowId),
      flowId,
      undefined,
      featureFlags,
      getActionTrackingInfo(cookies),
      datadog.logger,
    );

    if (!summaryResult.success || !summaryResult.responseBody) {
      return undefined;
    }

    return summaryResult.responseBody;
  }, [
    cookies,
    datadog.logger,
    discountCodes,
    flowId,
    formsActionsAPI,
    posthog.featureFlags,
    summaryAction,
  ]);

  const trackAddedNibblesAddToCart = useCallback(async () => {
    try {
      const planSummaryResponse = await getPlanSummaryForAddToCart();
      if (!planSummaryResponse) {
        return;
      }

      await tracking.addToCart({
        planSummaryResponse,
        ...getFlowContactDetails(),
        formName: flowId,
        gtmDataEvent: "added_nibbles",
      });
    } catch (error) {
      datadog.logger.warn("Failed to track added_nibbles addToCart", {
        flowId,
        summaryAction,
      }, error as Error);
    }
  }, [ datadog.logger, flowId, getFlowContactDetails, getPlanSummaryForAddToCart, summaryAction, tracking ]);

  const onToggleInBasket = useCallback((addToBasket: boolean, fieldName: string, quantity: number) => {
    const updatedQuantity: FieldData = addToBasket ?
      { submitValue: quantity, displayValue: quantity } :
      { submitValue: 0, displayValue: 0 };
    setFlowFieldValue(flowId, fieldName, updatedQuantity, linkingId);
    setFlowFieldValue(flowId, "addedNibbles", { submitValue: addToBasket }, linkingId);
    setIsInBasket(addToBasket);
    if (addToBasket) {
      next();
    }
  }, [ flowId, linkingId, next, setFlowFieldValue ]);

  const getIsProductInBasket = useCallback((fieldName: string): boolean => {
    const flow = getCurrentFlow(flowId);
    const foundField = flow?.fields?.find(
      (mappedField) => mappedField.key.fieldName === fieldName && mappedField.key.linkingId === linkingId,
    );
    if (!foundField) {
      return false;
    }
    return Number(foundField.data.submitValue) > 0;
  }, [ flowId, linkingId ]);

  const productToDisplay = useMemo(() => {
    const currentFlow = getCurrentFlow(flowId);
    const [ first ] = getAddProductProductsToDisplay(
      field,
      currentFlow?.fields,
    );
    if (!first) {
      return undefined;
    }
    return {
      ...first,
      title: first.title ?? defaultProductTitle[first.field_name],
      fieldName: first.field_name,
      imageTagText: first.image_tag_text,
      images: first.images.map((img) => ({
        image: getStoryBlokImage(img, true), alt: first.field_name })),
      allowAddProduct: false,
      isProductAdded: isInBasket ?? getIsProductInBasket(first.field_name),
      price: first.price,
      pricePrefix: first.price_prefix,
      salePrice: first.sale_price,
      addCtaText: first.add_cta_text || "Add to box",
      removeCtaText: first.remove_cta_text || "Remove",
      tag: first.tag,
      footer: renderStoryBlokRichText(first.footer),
      body: renderStoryBlokRichText(first.body),
      onRemoveProduct: () => onToggleInBasket(false, first.field_name, 1),
      icons: first.icons?.map((icon) => ({
        image: getStoryBlokImage(icon.image, true, 50),
        title: icon.title,
      })),
      comparisonCardsTitle: first.comparison_cards_title,
      comparisonCards: {
        cards: first.comparison_cards?.[0]?.cards?.map((card, cardIndex) => ({
          image: getStoryBlokImage(card.image, true, ImageMax.HALF_CONTAINER_MD),
          theme: card.override_theme ? card.override_theme : cardIndex === 0 ? "green" : "dark",
          title: card.title,
          tag: card.tag ? { theme: "pink", children: card.tag } : undefined,
          listItems: card.list_items.map((item) => ({
            title: item.title,
            icon: item.icon ? item.icon : cardIndex === 0 ? "tickHeavy" : "crossHeavy",
          })),
        })) as [IComparisonCardProps, IComparisonCardProps],
      },
    };
  }, [ field, flowId, getIsProductInBasket, isInBasket, onToggleInBasket ]);

  const onAddProductToBasket = () => {
    if (!productToDisplay) {
      return;
    }
    onToggleInBasket(true, productToDisplay.fieldName, 1);
    void trackAddedNibblesAddToCart();
    posthog.capture("addNibblesToBasket", { type: productToDisplay.fieldName });
  };

  if (!productToDisplay) {
    return null;
  }

  return (
    <Column>
      <>
        { productToDisplay.component === "form_add_product_card" && (
          <>
            { field.title ? (
              <>
                <TextSubtitle tag="h3" align="center" size={1}>
                  { interpolateString(field.title ?? "", getFlowFieldValuesForInterpolation(flowId, linkingId, "GET_ALL_WITH_REMOVED"), Interpolator.FRONTEND) }
                </TextSubtitle>
                <Spacer size="md" />
              </>
            ) : null }
            <SectionOneClickCard { ...productToDisplay } showFiftyOffSticker />

          </>
        ) }
        { productToDisplay.component === "form_add_product_card_b" && (
          <SectionOneClickCardB { ...productToDisplay } />
        ) }
        <div className={`fixed bottom-0 left-0 w-full p-4 z-[35] ${themeRootClassMap.brand}`}>
          { getIsProductInBasket(productToDisplay.fieldName) ?
            <Column align="center" justify={"center"} direction={"row"}>
              <Button
                onClick={next}
                design="default"
              >
                { "Continue" }
              </Button>
            </Column>
            :
            <Grid >
              <Column align="center" justify={"center"} direction={"row"}>
                <Button onClick={() => next()} design="underline" data-testid="continueWithoutProductCta">
                  <span className="flex justify-center items-center">No Thanks</span>
                </Button>
                <Button onClick={onAddProductToBasket} data-testid="continueWithProductCta">
                  <span className="flex justify-center items-center">Add To box</span>
                </Button>
              </Column>
            </Grid>
          }
        </div>
      </>
    </Column>
  );

};
